import AsyncStorage from "@react-native-async-storage/async-storage";
import { useGlobalSearchParams } from "expo-router";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type PropsWithChildren
} from "react";
import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  query,
  setDoc,
  where
} from "firebase/firestore";
import { Platform } from "react-native";

import type {
  ActivatedDevice,
  DancerRef,
  DeleteFamilyAccountResponse,
  FamilyAccountBilling,
  RecitalResumeState,
  ResumeState,
  SavedDancer
} from "@recital-master/shared";
import { firestorePaths } from "@recital-master/shared";

import { useAuthState } from "@/auth/AuthProvider";
import { getClientDeviceIdentity } from "@/devices/deviceIdentity";
import {
  deleteFamilyAccount,
  firebaseErrorCode,
  firebaseErrorMessage,
  listDevices
} from "@/firebase/callables";
import { firestore, hasFirebaseClientConfig } from "@/firebase/client";
import { useReadinessHold } from "@/providers/ReadinessGate";
import {
  normalizeRecitalResumeState,
  normalizeResumeState,
  recitalResumeDocumentForWrite,
  resumeDocumentForWrite
} from "@/data/accountPersistence";

/**
 * Family-account state the viewer owns: saved My Dancers and per-performance
 * resume positions. Persisted locally for now (web localStorage); the same
 * shape syncs to familyAccounts/{uid}/dancers and /resume in Firestore once
 * auth-gated data lands.
 */

type AccountState = {
  savedDancers: SavedDancer[];
  resume: Record<string, ResumeState>;
  recitalResume: Record<string, RecitalResumeState>;
  accessExpiresAt: string | null;
  /** Explicit server-owned download schedule overrides, keyed by recital. */
  downloadsUnlockAtByRecital: Record<string, string>;
  billing: FamilyAccountBilling | null;
  devices: ActivatedDevice[];
  currentDeviceId: string | null;
  deleteAccount: () => Promise<DeleteFamilyAccountResponse>;
  refreshDevices: () => Promise<void>;
  saveDancer: (dancer: DancerRef, recitalId: string) => void;
  removeDancer: (dancerId: string) => void;
  setResume: (state: ResumeState) => void;
  setRecitalResume: (state: RecitalResumeState) => void;
  clearResume: (performanceId: string) => void;
};

export class AccountDeletionError extends Error {
  readonly code: string | null;

  constructor(error: unknown) {
    super(firebaseErrorMessage(error), { cause: error });
    this.name = "AccountDeletionError";
    this.code = firebaseErrorCode(error);
  }
}

const AccountContext = createContext<AccountState | null>(null);

const STORAGE_KEY = "recital-master.account.v1";
const DEMO_STORAGE_KEY = "recital-master.demo-account.v2";

type PersistedShape = {
  savedDancers: SavedDancer[];
  resume: Record<string, ResumeState>;
  recitalResume: Record<string, RecitalResumeState>;
};

type AccountSession = {
  persisted: PersistedShape;
  accessExpiresAt: string | null;
  downloadsUnlockAtByRecital: Record<string, string>;
  billing: FamilyAccountBilling | null;
  devices: ActivatedDevice[];
  currentDeviceId: string | null;
};

const accountSessions = new Map<string, AccountSession>();

function normalizePersistedShape(
  value: Partial<PersistedShape> | null | undefined
): PersistedShape {
  const resume = Object.fromEntries(
    Object.entries(value?.resume ?? {}).flatMap(([id, state]) => {
      const normalized = normalizeResumeState(state, id);
      return normalized ? [[normalized.performanceId, normalized]] : [];
    })
  );
  const recitalResume = Object.fromEntries(
    Object.entries(value?.recitalResume ?? {}).flatMap(([id, state]) => {
      const normalized = normalizeRecitalResumeState(state, id);
      return normalized ? [[normalized.recitalId, normalized]] : [];
    })
  );
  return {
    savedDancers: value?.savedDancers ?? [],
    resume,
    recitalResume
  };
}

async function loadDevices(): Promise<{
  devices: ActivatedDevice[];
  currentDeviceId: string;
}> {
  if (!firestore) return { devices: [], currentDeviceId: "" };
  const [identity, response] = await Promise.all([
    getClientDeviceIdentity(),
    listDevices()
  ]);
  return {
    devices: response.devices,
    currentDeviceId: identity.deviceId
  };
}

function readPersisted(): PersistedShape {
  try {
    if (typeof localStorage !== "undefined") {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as Partial<PersistedShape>;
        return normalizePersistedShape(parsed);
      }
    }
  } catch {
    // Corrupt or unavailable storage falls through to empty state.
  }
  return { savedDancers: [], resume: {}, recitalResume: {} };
}

function writePersisted(shape: PersistedShape) {
  try {
    if (typeof localStorage !== "undefined") {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(shape));
    }
  } catch {
    // Best-effort in dev.
  }
}

export function AccountProvider({ children }: PropsWithChildren) {
  const { user, demoMode, signOutUser } = useAuthState();
  const { download_preview: downloadPreview } = useGlobalSearchParams<{
    download_preview?: string;
  }>();
  const restoredSession = user ? accountSessions.get(user.uid) : null;
  const [persisted, setPersisted] = useState<PersistedShape>(
    () => restoredSession?.persisted ?? readPersisted()
  );
  const [accessExpiresAt, setAccessExpiresAt] = useState<string | null>(
    restoredSession?.accessExpiresAt ?? null
  );
  const [downloadsUnlockAtByRecital, setDownloadsUnlockAtByRecital] = useState<
    Record<string, string>
  >(restoredSession?.downloadsUnlockAtByRecital ?? {});
  const [billing, setBilling] = useState<FamilyAccountBilling | null>(
    restoredSession?.billing ?? null
  );
  const [devices, setDevices] = useState<ActivatedDevice[]>(restoredSession?.devices ?? []);
  const [currentDeviceId, setCurrentDeviceId] = useState<string | null>(
    restoredSession?.currentDeviceId ?? null
  );
  const [loadedUserUid, setLoadedUserUid] = useState<string | null>(
    restoredSession && user ? user.uid : null
  );
  const [demoPersistedReady, setDemoPersistedReady] = useState(!demoMode);
  const resumeWriteTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  const previousDemoModeRef = useRef(demoMode);
  const demoStorageLoadedRef = useRef(false);
  const firebaseMode =
    !demoMode && hasFirebaseClientConfig && user != null && firestore != null;
  const accountReady = demoMode
    ? demoPersistedReady && demoStorageLoadedRef.current
    : !firebaseMode || loadedUserUid === user?.uid;
  useReadinessHold("account", accountReady);

  useEffect(() => {
    let cancelled = false;
    if (!demoMode) {
      if (previousDemoModeRef.current) setPersisted(readPersisted());
      previousDemoModeRef.current = false;
      demoStorageLoadedRef.current = false;
      setDemoPersistedReady(true);
      return undefined;
    }

    previousDemoModeRef.current = true;
    demoStorageLoadedRef.current = false;
    setDemoPersistedReady(false);
    setAccessExpiresAt(null);
    setDownloadsUnlockAtByRecital({});
    setBilling(null);
    setDevices([]);
    setCurrentDeviceId(null);
    AsyncStorage.getItem(DEMO_STORAGE_KEY)
      .then((raw) => {
        if (cancelled) return;
        const parsed = raw ? (JSON.parse(raw) as Partial<PersistedShape>) : null;
        setPersisted(normalizePersistedShape(parsed));
      })
      .catch(() => {
        if (!cancelled) {
          setPersisted({ savedDancers: [], resume: {}, recitalResume: {} });
        }
      })
      .finally(() => {
        if (!cancelled) {
          demoStorageLoadedRef.current = true;
          setDemoPersistedReady(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [demoMode]);

  useEffect(() => {
    if (user && loadedUserUid === user.uid) {
      accountSessions.set(user.uid, {
        persisted,
        accessExpiresAt,
        downloadsUnlockAtByRecital,
        billing,
        devices,
        currentDeviceId
      });
    }
  }, [
    accessExpiresAt,
    billing,
    currentDeviceId,
    devices,
    downloadsUnlockAtByRecital,
    loadedUserUid,
    persisted,
    user
  ]);

  useEffect(() => {
    if (demoMode) {
      if (demoPersistedReady && demoStorageLoadedRef.current) {
        void AsyncStorage.setItem(DEMO_STORAGE_KEY, JSON.stringify(persisted));
      }
      return;
    }
    if (!firebaseMode) writePersisted(persisted);
  }, [demoMode, demoPersistedReady, firebaseMode, persisted]);

  useEffect(
    () => () => {
      for (const timer of Object.values(resumeWriteTimers.current)) {
        clearTimeout(timer);
      }
    },
    []
  );

  useEffect(() => {
    if (!firebaseMode || !user || !firestore) {
      setLoadedUserUid(null);
      return undefined;
    }
    if (loadedUserUid === user.uid) return undefined;
    const cached = accountSessions.get(user.uid);
    if (cached) {
      setPersisted(cached.persisted);
      setAccessExpiresAt(cached.accessExpiresAt);
      setDownloadsUnlockAtByRecital(cached.downloadsUnlockAtByRecital ?? {});
      setBilling(cached.billing);
      setDevices(cached.devices);
      setCurrentDeviceId(cached.currentDeviceId);
      setLoadedUserUid(user.uid);
      return undefined;
    }
    let cancelled = false;
    setLoadedUserUid(null);
    (async () => {
      const local = readPersisted();
      const [
        accountSnapshot,
        grantsSnapshot,
        dancersSnapshot,
        resumeSnapshot,
        recitalResumeSnapshot,
        deviceState
      ] = await Promise.all([
        getDoc(doc(firestore!, firestorePaths.familyAccount(user.uid))),
        getDocs(collection(firestore!, `familyAccounts/${user.uid}/grants`)),
        getDocs(collection(firestore!, `familyAccounts/${user.uid}/dancers`)),
        getDocs(collection(firestore!, `familyAccounts/${user.uid}/resume`)),
        getDocs(collection(firestore!, `familyAccounts/${user.uid}/recitalResume`)),
        loadDevices()
      ]);
      if (cancelled) return;

      const remoteDancers = dancersSnapshot.docs.map((entry) => entry.data() as SavedDancer);
      const remoteResume = Object.fromEntries(
        resumeSnapshot.docs.flatMap((entry) => {
          const normalized = normalizeResumeState(entry.data(), entry.id);
          return normalized ? [[normalized.performanceId, normalized]] : [];
        })
      );
      const remoteRecitalResume = Object.fromEntries(
        recitalResumeSnapshot.docs.flatMap((entry) => {
          const normalized = normalizeRecitalResumeState(entry.data(), entry.id);
          return normalized ? [[normalized.recitalId, normalized]] : [];
        })
      );
      const remoteEmpty =
        remoteDancers.length === 0 &&
        Object.keys(remoteResume).length === 0 &&
        Object.keys(remoteRecitalResume).length === 0;

      if (
        remoteEmpty &&
        (local.savedDancers.length > 0 ||
          Object.keys(local.resume).length > 0 ||
          Object.keys(local.recitalResume).length > 0)
      ) {
        await Promise.all([
          ...local.savedDancers.map((dancer) =>
            setDoc(doc(firestore!, firestorePaths.savedDancer(user.uid, dancer.dancerId)), dancer)
          ),
          ...Object.values(local.resume).map((resume) =>
            setDoc(
              doc(firestore!, firestorePaths.resume(user.uid, resume.performanceId)),
              resumeDocumentForWrite(resume)
            )
          ),
          ...Object.values(local.recitalResume).map((resume) =>
            setDoc(
              doc(firestore!, firestorePaths.recitalResume(user.uid, resume.recitalId)),
              recitalResumeDocumentForWrite(resume)
            )
          )
        ]);
        if (!cancelled) setPersisted(local);
      } else {
        setPersisted({
          savedDancers: remoteDancers,
          resume: remoteResume,
          recitalResume: remoteRecitalResume
        });
      }
      if (!cancelled) {
        setAccessExpiresAt(
          (accountSnapshot.get("accessExpiresAt") as string | null | undefined) ?? null
        );
        setDownloadsUnlockAtByRecital(
          Object.fromEntries(
            grantsSnapshot.docs.flatMap((entry) => {
              const downloadsUnlockAt = entry.get("downloadsUnlockAt");
              return entry.get("revokedAt") == null && typeof downloadsUnlockAt === "string"
                ? [[entry.id, downloadsUnlockAt]]
                : [];
            })
          )
        );
        setBilling(
          (accountSnapshot.get("billing") as FamilyAccountBilling | null | undefined) ?? null
        );
        setDevices(deviceState.devices);
        setCurrentDeviceId(deviceState.currentDeviceId);
        setLoadedUserUid(user.uid);
      }
    })().catch(() => {
      if (!cancelled) setLoadedUserUid(user.uid);
    });
    return () => {
      cancelled = true;
    };
  }, [firebaseMode, loadedUserUid, user]);

  // Bootstrap above composes a complete first account render. Once ready,
  // keep only the mutable family data live so another signed-in device can
  // add/remove devices, dancers, or resume progress without a page refresh.
  useEffect(() => {
    if (!firebaseMode || !user || !firestore || loadedUserUid !== user.uid) return;

    let active = true;
    void getClientDeviceIdentity().then((identity) => {
      if (active) setCurrentDeviceId(identity.deviceId);
    });

    const ignoreListenerError = () => {
      // The complete bootstrap remains usable while Firestore reconnects.
    };
    const unsubscribeAccount = onSnapshot(
      doc(firestore, firestorePaths.familyAccount(user.uid)),
      (snapshot) => {
        setAccessExpiresAt(
          snapshot.exists()
            ? ((snapshot.get("accessExpiresAt") as string | null | undefined) ?? null)
            : null
        );
        setBilling(
          snapshot.exists()
            ? ((snapshot.get("billing") as FamilyAccountBilling | null | undefined) ?? null)
            : null
        );
      },
      ignoreListenerError
    );
    const unsubscribeGrants = onSnapshot(
      collection(firestore, `familyAccounts/${user.uid}/grants`),
      (snapshot) => {
        setDownloadsUnlockAtByRecital(
          Object.fromEntries(
            snapshot.docs.flatMap((entry) => {
              const downloadsUnlockAt = entry.get("downloadsUnlockAt");
              return entry.get("revokedAt") == null && typeof downloadsUnlockAt === "string"
                ? [[entry.id, downloadsUnlockAt]]
                : [];
            })
          )
        );
      },
      ignoreListenerError
    );
    const unsubscribeDancers = onSnapshot(
      collection(firestore, `familyAccounts/${user.uid}/dancers`),
      (snapshot) => {
        const savedDancers = snapshot.docs
          .map((entry) => entry.data() as SavedDancer)
          .sort((a, b) => a.savedAt.localeCompare(b.savedAt));
        setPersisted((current) => ({ ...current, savedDancers }));
      },
      ignoreListenerError
    );
    const unsubscribeResume = onSnapshot(
      collection(firestore, `familyAccounts/${user.uid}/resume`),
      (snapshot) => {
        const resume = Object.fromEntries(
          snapshot.docs.flatMap((entry) => {
            const normalized = normalizeResumeState(entry.data(), entry.id);
            return normalized ? [[normalized.performanceId, normalized]] : [];
          })
        );
        setPersisted((current) => ({ ...current, resume }));
      },
      ignoreListenerError
    );
    const unsubscribeRecitalResume = onSnapshot(
      collection(firestore, `familyAccounts/${user.uid}/recitalResume`),
      (snapshot) => {
        const recitalResume = Object.fromEntries(
          snapshot.docs.flatMap((entry) => {
            const normalized = normalizeRecitalResumeState(entry.data(), entry.id);
            return normalized ? [[normalized.recitalId, normalized]] : [];
          })
        );
        setPersisted((current) => ({ ...current, recitalResume }));
      },
      ignoreListenerError
    );
    const unsubscribeDevices = onSnapshot(
      query(
        collection(firestore, `familyAccounts/${user.uid}/devices`),
        where("removedAt", "==", null)
      ),
      () => {
        void listDevices()
          .then((response) => {
            if (active) setDevices(response.devices);
          })
          .catch(() => {
            // Keep the last complete device list while Functions reconnects.
          });
      },
      ignoreListenerError
    );

    return () => {
      active = false;
      unsubscribeAccount();
      unsubscribeGrants();
      unsubscribeDancers();
      unsubscribeResume();
      unsubscribeRecitalResume();
      unsubscribeDevices();
    };
  }, [firebaseMode, loadedUserUid, user]);

  const refreshDevices = useCallback(async () => {
    if (demoMode || !user || !firestore) return;
    const next = await loadDevices();
    setDevices(next.devices);
    setCurrentDeviceId(next.currentDeviceId);
  }, [demoMode, user]);

  const deleteAccount = useCallback(async (): Promise<DeleteFamilyAccountResponse> => {
    if (demoMode || !user) {
      throw new AccountDeletionError(
        Object.assign(new Error("Sign in to delete an account."), {
          code: "functions/failed-precondition"
        })
      );
    }

    for (const timer of Object.values(resumeWriteTimers.current)) {
      clearTimeout(timer);
    }
    resumeWriteTimers.current = {};

    try {
      const result = await deleteFamilyAccount();
      accountSessions.delete(user.uid);
      try {
        if (typeof localStorage !== "undefined") localStorage.removeItem(STORAGE_KEY);
      } catch {
        // Auth sign-out still clears account-scoped cached artwork.
      }
      setPersisted({ savedDancers: [], resume: {}, recitalResume: {} });
      setAccessExpiresAt(null);
      setDownloadsUnlockAtByRecital({});
      setBilling(null);
      setDevices([]);
      setCurrentDeviceId(null);
      await signOutUser();
      return result;
    } catch (error) {
      if (error instanceof AccountDeletionError) throw error;
      throw new AccountDeletionError(error);
    }
  }, [demoMode, signOutUser, user]);

  const saveDancer = useCallback((dancer: DancerRef, recitalId: string) => {
    const saved: SavedDancer = {
      ...dancer,
      recitalId,
      savedAt: new Date().toISOString()
    };
    setPersisted((current) => {
      if (current.savedDancers.some((d) => d.dancerId === dancer.dancerId)) {
        return current;
      }
      return { ...current, savedDancers: [...current.savedDancers, saved] };
    });
    if (firebaseMode && user && firestore) {
      setDoc(doc(firestore, firestorePaths.savedDancer(user.uid, dancer.dancerId)), saved).catch(
        () => {}
      );
    }
  }, [firebaseMode, user]);

  const removeDancer = useCallback((dancerId: string) => {
    setPersisted((current) => ({
      ...current,
      savedDancers: current.savedDancers.filter((d) => d.dancerId !== dancerId)
    }));
    if (firebaseMode && user && firestore) {
      deleteDoc(doc(firestore, firestorePaths.savedDancer(user.uid, dancerId))).catch(() => {});
    }
  }, [firebaseMode, user]);

  const setResume = useCallback((state: ResumeState) => {
    setPersisted((current) => ({
      ...current,
      resume: { ...current.resume, [state.performanceId]: state }
    }));
    if (firebaseMode && user && firestore) {
      const timerKey = `performance:${state.performanceId}`;
      if (resumeWriteTimers.current[timerKey]) {
        clearTimeout(resumeWriteTimers.current[timerKey]);
      }
      resumeWriteTimers.current[timerKey] = setTimeout(() => {
        setDoc(
          doc(firestore!, firestorePaths.resume(user.uid, state.performanceId)),
          resumeDocumentForWrite(state)
        ).catch(() => {});
      }, 5000);
    }
  }, [firebaseMode, user]);

  const setRecitalResume = useCallback((state: RecitalResumeState) => {
    setPersisted((current) => ({
      ...current,
      recitalResume: {
        ...current.recitalResume,
        [state.recitalId]: state
      }
    }));
    if (firebaseMode && user && firestore) {
      const timerKey = `recital:${state.recitalId}`;
      if (resumeWriteTimers.current[timerKey]) {
        clearTimeout(resumeWriteTimers.current[timerKey]);
      }
      resumeWriteTimers.current[timerKey] = setTimeout(() => {
        setDoc(
          doc(firestore!, firestorePaths.recitalResume(user.uid, state.recitalId)),
        recitalResumeDocumentForWrite(state)
      ).catch(() => {});
      }, 5000);
    }
  }, [firebaseMode, user]);

  const clearResume = useCallback((performanceId: string) => {
    const timerKey = `performance:${performanceId}`;
    const pendingWrite = resumeWriteTimers.current[timerKey];
    if (pendingWrite) {
      clearTimeout(pendingWrite);
      delete resumeWriteTimers.current[timerKey];
    }
    setPersisted((current) => {
      const resume = { ...current.resume };
      delete resume[performanceId];
      return { ...current, resume };
    });
    if (firebaseMode && user && firestore) {
      deleteDoc(doc(firestore, firestorePaths.resume(user.uid, performanceId))).catch(() => {});
    }
  }, [firebaseMode, user]);

  const previewAccessExpiresAt = useMemo(() => {
    if (!__DEV__ || Platform.OS !== "web" || !downloadPreview) return null;
    const date = new Date();
    if (downloadPreview === "unlocked") date.setDate(date.getDate() + 15);
    else if (downloadPreview === "locked") date.setDate(date.getDate() + 365);
    else if (downloadPreview === "expired") date.setDate(date.getDate() - 1);
    else return null;
    return date.toISOString();
  }, [downloadPreview]);

  const value = useMemo<AccountState>(
    () => ({
      savedDancers: persisted.savedDancers,
      resume: persisted.resume,
      recitalResume: persisted.recitalResume,
      accessExpiresAt: previewAccessExpiresAt ?? accessExpiresAt,
      downloadsUnlockAtByRecital,
      billing,
      devices,
      currentDeviceId,
      deleteAccount,
      refreshDevices,
      saveDancer,
      removeDancer,
      setResume,
      setRecitalResume,
      clearResume
    }),
    [
      accessExpiresAt,
      billing,
      clearResume,
      currentDeviceId,
      deleteAccount,
      devices,
      downloadsUnlockAtByRecital,
      persisted,
      previewAccessExpiresAt,
      refreshDevices,
      removeDancer,
      saveDancer,
      setRecitalResume,
      setResume
    ]
  );

  return <AccountContext.Provider value={value}>{children}</AccountContext.Provider>;
}

export function useAccount(): AccountState {
  const context = useContext(AccountContext);
  if (!context) throw new Error("useAccount requires AccountProvider");
  return context;
}
